import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";

declare global {
  interface Window {
    __SANITY_CONFIG__?: { SANITY_PROJECT_ID?: string; SANITY_DATASET?: string };
  }
}

function getSanityConfig(): { projectId: string; dataset: string } {
  if (typeof window !== "undefined" && window.__SANITY_CONFIG__ && typeof window.__SANITY_CONFIG__ === "object") {
    const c = window.__SANITY_CONFIG__;
    return {
      projectId: c.SANITY_PROJECT_ID ?? "0o5uiojn",
      dataset: c.SANITY_DATASET ?? "production",
    };
  }
  return {
    projectId: typeof process !== "undefined" ? process.env.SANITY_PROJECT_ID ?? "0o5uiojn" : "0o5uiojn",
    dataset: typeof process !== "undefined" ? process.env.SANITY_DATASET ?? "production" : "production",
  };
}

const { projectId, dataset } = getSanityConfig();

// Use proxy to avoid CORS (browser -> our server -> Sanity).
export async function fetchQuery<T>(query: string, params?: Record<string, unknown>): Promise<T> {
  const res = await fetch("/api/sanity/query", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, params }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Sanity query failed");
  }
  return res.json() as Promise<T>;
}

// Client still used for image URLs (no CORS).
const clientForImages = createClient({
  projectId,
  dataset,
  apiVersion: "2024-01-01",
  useCdn: true,
});
const builder = imageUrlBuilder(clientForImages);

export type SanityImage = {
  _type: "image";
  asset?: { _ref?: string; _id?: string; url?: string };
  hotspot?: unknown;
  crop?: unknown;
  /** Resolved from GROQ: asset->url */
  imageUrl?: string | null;
};

export type PostCategory = {
  slug: { current: string };
  title?: string | null;
};

export type Post = {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt?: string | null;
  mainImage?: SanityImage | null;
  publishedAt?: string | null;
  body?: unknown[] | null;
  categories?: PostCategory[] | null;
};

const postFields = `
  _id,
  title,
  slug,
  excerpt,
  mainImage {
    ...,
    "imageUrl": asset->url
  },
  publishedAt,
  body,
  "categories": categories[]->{ slug, title }
`;

export const postsQuery = `*[_type == "post"] | order(publishedAt desc) {
  ${postFields}
}`;

export const postBySlugQuery = `*[_type == "post" && slug.current == $slug][0] {
  ${postFields}
}`;

export const postsByCategoryQuery = `*[_type == "post" && $categorySlug in categories[]->slug.current] | order(publishedAt desc) {
  ${postFields}
}`;

export type Category = {
  _id: string;
  title?: string | null;
  slug: { current: string };
};

export const categoryBySlugQuery = `*[_type == "category" && slug.current == $slug][0] {
  _id,
  title,
  slug
}`;

export function imageBuilder(image: SanityImage | null | undefined, width?: number): string {
  if (!image) return "";
  if (image.imageUrl) return image.imageUrl;
  const ref = image.asset?._ref ?? image.asset?._id;
  if (!ref) return "";
  return builder.image({ ...image, asset: { _ref: ref } }).width(width ?? 1200).url();
}
